'use client'
import Image from 'next/image'

import { clsx } from 'clsx'

import { FC, useEffect, useState } from 'react'

import { Maybe, TrainingCourse } from '@/app/graphql/generated'
import { AnimationMoveIconComponent } from '@/app/shared/components/animation/move-icon'
import { beforeElement } from '@/app/shared/components/cours-item/contants'
import {
  colorThemeIcon,
  generateDescription,
  getTitles,
} from '@/app/shared/components/cours-item/services/course-item.services'
import SignUpLessonComponent from '@/app/shared/components/modals/sign-up-lesson/sign-up-lesson.component'
import { IconArrowSecond, IconBgShort, IconBgShortSmall, IconCheck } from '@/app/shared/icons'
import { ImageLetsGo } from '@/app/shared/images'
import { clearList } from '@/app/shared/services/clearList'
import useLayout from '@/app/shared/store/layout.store'
import useModal from '@/app/shared/store/modals.store'
import { AnimationHeightComponent } from '@/app/shared/ui/animation-height'
import { ButtonComponent } from '@/app/shared/ui/button'
import { t } from '@/core/lib/locale/i18n'

// interface
interface ICourse {
  data?: Maybe<TrainingCourse>
  type: 'standard' | 'intensive'
  sticker?: string
  buttonText?: string
}

// component
const CourseItemComponent: FC<Readonly<ICourse>> = ({ data, type, sticker, buttonText }) => {
  const colorTheme = type === 'standard' ? 'text-secondary' : 'text-danger'
  const bgTheme = type === 'standard' ? 'bg-secondary' : 'bg-danger'
  const borderColor = type === 'standard' ? 'border-secondary' : 'border-danger'
  const colorBtn = type === 'standard' ? 'secondary' : 'danger'
  const share = type === 'standard' ? 'text-danger' : 'text-secondary'

  const [courseDetails, setCourseDetails] = useState<string[]>([])
  const [controlDetails, setControlDetails] = useState<string[]>([])
  const [neededDetails, setNeededDetails] = useState<string[]>([])

  const [isDesktop, setIsDesktop] = useState(false)
  const open = useModal((state) => state.open)
  const typeSite = useLayout((state) => state.typeSite)

  const description = generateDescription(data)

  const handleOpenModal = () => {
    open({
      component: SignUpLessonComponent,
      props: {
        title: buttonText ?? t.sign_up_text(),
      },
    })
  }

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024)
    }

    handleResize()

    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setCourseDetails(clearList(data?.course_details ?? ''))
  }, [data?.course_details])

  useEffect(() => {
    setControlDetails(clearList(data?.knowledge_control ?? ''))
  }, [data?.knowledge_control])

  useEffect(() => {
    setNeededDetails(clearList(data?.as_needed ?? ''))
  }, [data?.as_needed])

  // return
  return (
    <div
      className={clsx('rounded-large border-2 bg-white pt-10', colorTheme, borderColor, {
        'pb-[30px]': isDesktop,
      })}
    >
      <div className={'relative grid px-[30px]'}>
        <div className={'absolute -left-[18px] -top-[42px] h-[42px] px-[5px]'}>
          <span className={'relative z-[1] text-[10px] font-semibold uppercase text-content1-200'}>
            {typeSite === 'online' || typeSite === 'offline'
              ? t.from_beginner_to_advanced()
              : typeSite === 'online-deutsch' || typeSite === 'kids'
                ? t.from_a1_to_c1()
                : t.from_a1_to_b2()}
          </span>
          {typeSite === 'online' || typeSite === 'offline' ? (
            <IconBgShort className={clsx('absolute left-0 top-0 h-full w-full', share)} />
          ) : (
            <IconBgShortSmall className={clsx('absolute left-0 top-0 h-[42px]', share)} />
          )}
        </div>
        <div className={'mb-10 flex flex-wrap items-center justify-between'}>
          <h2 className={'text-2xl font-extrabold laptop:text-[32px] large:text-3xl'}>
            {data?.title}
          </h2>
          <AnimationMoveIconComponent
            className={'relative h-[62px] w-[79px] flex-shrink-0 laptop:h-[114px] laptop:w-[145px]'}
            valueMoving={55}
          >
            <Image
              src={sticker ?? ImageLetsGo}
              alt={'courses'}
              fill
              sizes={'(max-width: 1024px):79px, 145px'}
            />
          </AnimationMoveIconComponent>
        </div>
        <ul className={'mb-5 grid grid-cols-2 laptop:flex'}>
          {description.map((el, index: number) => (
            <li
              className={clsx(
                'relative grid w-full justify-center gap-[5px]',
                beforeElement,
                {
                  '[&:not(:last-child)]:before:bg-secondary': type === 'standard',
                },
                {
                  '[&:not(:last-child)]:before:bg-danger': type !== 'standard',
                },
              )}
              key={index}
            >
              <span className={'text-center text-[14px] font-semibold uppercase'}>
                {getTitles(el.slug)}
              </span>
              <span
                className={
                  'grid max-w-[355px] text-center text-[14px] text-black laptop:text-base large:text-[22px]'
                }
              >
                {el.value}
              </span>
            </li>
          ))}
        </ul>
        {!!data?.amount_of_study_hours && (
          <div
            className={
              'mb-[33px] flex items-center justify-center gap-x-2.5 desktop:flex desktop:gap-0'
            }
          >
            <span className={'text-center text-[22px] font-extrabold desktop:text-left'}>
              {typeSite === 'kids' ? t.training_hours_course() : t.training_hours()}
            </span>
            <span className={'ml-1.5 text-[16px] text-black desktop:text-[22px]'}>
              {data.amount_of_study_hours}
            </span>
          </div>
        )}
      </div>
      {!!data?.price_of_education && (
        <div
          className={clsx(
            bgTheme,
            'mb-[30px] grid justify-center gap-2.5 p-3 desktop:mb-10',
            {
              'text-white': typeSite !== 'online-deutsch',
            },
            {
              '!text-black':
                typeSite === 'online-deutsch' ||
                (typeSite === 'online-espanol' && type === 'intensive'),
            },
          )}
        >
          <div
            className={
              'flex flex-col items-center gap-5 text-center text-2xl font-extrabold desktop:gap-0 desktop:text-left'
            }
          >
            <span
              className={clsx(
                'm-auto text-center max-tablet:max-w-[523px]',
                {
                  'text-black': typeSite === 'online' || typeSite === 'offline',
                },
                {
                  'text-primary':
                    type === 'standard' &&
                    (typeSite === 'online-espanol' || typeSite === 'online-deutsch'),
                },
                {
                  'text-secondary':
                    type === 'intensive' &&
                    (typeSite === 'online-espanol' || typeSite === 'online-deutsch'),
                },
              )}
            >
              {data?.price_of_education}
            </span>
          </div>

          <div
            className={clsx(
              'inline text-center before:mr-1 before:mt-1 [&_p]:inline',
              {
                'before:content-whiteTick':
                  (typeSite !== 'online-deutsch' && type === 'standard') ||
                  !(typeSite === 'online-espanol' && type === 'intensive'),
              },
              {
                'before:content-blackTick':
                  typeSite === 'online-deutsch' ||
                  (typeSite === 'online-espanol' && type === 'intensive'),
              },
            )}
            dangerouslySetInnerHTML={{ __html: data?.base_price ?? '' }}
          ></div>
          <div
            className={clsx('grid w-full gap-4', {
              'laptop:grid-cols-2': data?.sale_1 && data?.sale_2,
            })}
          >
            {data?.sale_1 && (
              <div
                className={clsx(
                  'inline text-center before:mr-1 before:mt-1 [&_p]:inline',
                  {
                    'before:content-whiteTick':
                      (typeSite !== 'online-deutsch' && type === 'standard') ||
                      !(typeSite === 'online-espanol' && type === 'intensive'),
                  },
                  {
                    'before:content-blackTick':
                      typeSite === 'online-deutsch' ||
                      (typeSite === 'online-espanol' && type === 'intensive'),
                  },
                )}
                dangerouslySetInnerHTML={{ __html: data?.sale_1 ?? '' }}
              ></div>
            )}
            {data?.sale_2 && (
              <div
                className={clsx(
                  'inline text-center before:mr-1 before:mt-1 [&_p]:inline',
                  {
                    'before:content-whiteTick':
                      (typeSite !== 'online-deutsch' && type === 'standard') ||
                      !(typeSite === 'online-espanol' && type === 'intensive'),
                  },
                  {
                    'before:content-blackTick':
                      typeSite === 'online-deutsch' ||
                      (typeSite === 'online-espanol' && type === 'intensive'),
                  },
                )}
                dangerouslySetInnerHTML={{ __html: data?.sale_2 ?? '' }}
              ></div>
            )}
          </div>

          <span className={'text-center text-[14px] text-black large:text-base'}>{data?.note}</span>
        </div>
      )}
      <AnimationHeightComponent
        startHeight={isDesktop ? 'auto' : '10rem'}
        paddingBottom={isDesktop ? 0 : 60}
        className={'laptop:rounded-0 overflow-hidden rounded-[0_0_30px_30px] px-[30px]'}
        slotBtn={(handleToggle, open) => (
          <ButtonComponent
            variant={'light'}
            onClick={handleToggle}
            className={clsx(
              'absolute bottom-0 left-0 flex h-20 w-full items-center justify-center bg-gradient-to-b from-transparent via-content1-200 to-content1-200 text-[16px] font-extrabold uppercase',
              {
                hidden: isDesktop,
              },
            )}
          >
            {open ? t.hide_more() : t.show_more()}
            <IconArrowSecond
              className={clsx(
                'ml-2 h-[30px] w-[30px] rotate-45 text-secondary transition-transform',
                {
                  '!-rotate-45': open,
                },
              )}
            />
          </ButtonComponent>
        )}
      >
        <div className={'grid'}>
          <div className={'mb-[40px] grid gap-5'}>
            <span className={'text-2xl font-extrabold text-black'}>{t.course_contains()}</span>
            <ul className={'grid gap-x-[54px] gap-y-5 extraMobile:grid-cols-2'}>
              {courseDetails.map((str, index: number) => (
                <li key={index} className={'flex items-center desktop:text-[22px]'}>
                  <IconCheck className={'mr-2.5 h-[26px] w-[26px] flex-shrink-0'} />
                  <span className={'text-black'}>{str}</span>
                </li>
              ))}
            </ul>
          </div>
          {controlDetails?.length > 0 && (
            <div className={'mb-[40px] grid gap-5'}>
              <span className={'text-2xl font-extrabold text-black'}>{t.knowledge_control()}</span>
              <ul className={'grid gap-x-[54px] gap-y-5 extraMobile:grid-cols-2'}>
                {controlDetails.map((str, index: number) => (
                  <li key={index} className={'flex items-center desktop:text-[22px]'}>
                    <IconCheck className={'mr-2.5 h-[26px] w-[26px] flex-shrink-0'} />
                    <span className={'text-black'}>{str}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className={'mb-[50px] grid gap-5'}>
            <span className={'text-2xl font-extrabold text-black'}>{t.knowledge_control()}</span>

            {neededDetails.map((str, index: number) => (
              <li key={index} className={'flex items-center desktop:text-[22px]'}>
                <IconCheck className={'mr-2.5 h-[26px] w-[26px] flex-shrink-0'} />
                <span className={'text-black'}>{str}</span>
              </li>
            ))}
          </div>
          <ButtonComponent
            color={colorBtn}
            size={'md'}
            className={'group'}
            icon={`h-[32[x] ml-5 w-[32px] text-white ${colorThemeIcon(type)}`}
            onClick={handleOpenModal}
          >
            {buttonText ?? t.sign_up_text()}
          </ButtonComponent>
        </div>
      </AnimationHeightComponent>
    </div>
  )
}

export default CourseItemComponent
